import React from 'react';
import {
    XCircle,
    CheckCircle,
    AlertCircle,
    Calendar,
    Clock,
    Smartphone,
    Bell,
    User,
    Hash
} from 'lucide-react';

export default function FCMDetails({ selectedNotification, onClose }) {
    if (!selectedNotification) return null;

    const getStatusColor = (response, status) => {
        if (
            status === 'SUCCESS' ||
            response?.toLowerCase().includes('successfully') ||
            response?.toLowerCase().includes('sent successfully')
        ) return 'text-green-400';
        if (
            status === 'FAILED' ||
            response?.toLowerCase().includes('error') ||
            response?.toLowerCase().includes('invalid')
        ) return 'text-red-400';
        return 'text-yellow-400';
    };

    const getStatusIcon = (response, status) => {
        if (status === 'SUCCESS' || response?.includes('succesfully')) return CheckCircle;
        if (status === 'FAILED') return XCircle;
        return AlertCircle;
    };

    const StatusIcon = getStatusIcon(
        selectedNotification.fcmResponse,
        selectedNotification.STATUS
    );
    const statusColor = getStatusColor(
        selectedNotification.fcmResponse,
        selectedNotification.STATUS
    );

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-white/20 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                            <Bell className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-white">FCM Details</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-200"
                    >
                        <XCircle className="w-5 h-5 text-white" />
                    </button>
                </div>

                <div className="space-y-4">
                    {/* IDs & Location */}
                    <div className="grid grid-cols-2 gap-4">
                        {selectedNotification?.rf1_id && (
                            <div>
                                <p className="text-white/40 text-sm uppercase mb-1">Site Id</p>
                                <p className="text-white text-sm font-mono break-all">{selectedNotification.rf1_id}</p>
                            </div>
                        )}
                        <div>
                            <p className="text-white/40 text-sm uppercase mb-1">Site Name</p>
                            <p className="text-white text-sm font-mono">{selectedNotification.rf1_name || 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-white/40 text-sm uppercase mb-1">Notification ID</p>
                            <p className="text-white text-sm font-mono break-all">{selectedNotification._id}</p>
                        </div>
                        <div>
                            <p className="text-white/40 text-sm uppercase mb-1">Status</p>
                            <div className="flex items-center gap-2">
                                <StatusIcon className={`w-4 h-4 ${statusColor}`} />
                                <span className={`text-sm font-medium ${statusColor}`}>
                                    {selectedNotification.STATUS}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div>
                        <p className="text-white/40 text-sm uppercase mb-1">Title</p>
                        <p className="text-white">{selectedNotification.title}</p>
                    </div>

                    <div>
                        <p className="text-white/40 text-sm uppercase mb-1">Body</p>
                        <p className="text-white whitespace-pre-line">{selectedNotification.body}</p>
                    </div>

                    <div>
                        <p className="text-white/40 text-sm uppercase mb-1">Message</p>
                        <p className="text-white">{selectedNotification.message || '—'}</p>
                    </div>

                    {/* Tokens */}
                    {selectedNotification.fcm_type === 'BULK' && selectedNotification.bulk_token?.length > 0 ? (
                        <div>
                            <p className="text-white/40 text-sm uppercase mb-1">
                                Bulk Tokens ({selectedNotification.bulk_token.length})
                            </p>
                            <div className="max-h-48 overflow-y-auto bg-white/10 p-3 rounded-lg space-y-2">
                                {selectedNotification.bulk_token.map((token, index) => (
                                    <p key={index} className="text-white text-sm font-mono break-all">
                                        {index + 1}. {token}
                                    </p>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div>
                            <p className="text-white/40 text-sm uppercase mb-1">Device Token</p>
                            <p className="text-white text-sm font-mono break-all bg-white/10 p-3 rounded-lg">
                                {selectedNotification.device_token || 'N/A'}
                            </p>
                        </div>
                    )}

                    {/* Meta */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-white/40 text-sm uppercase mb-1">App Name</p>
                            <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-white/60" />
                                <p className="text-white">{selectedNotification.appName}</p>
                            </div>
                        </div>
                        <div>
                            <p className="text-white/40 text-sm uppercase mb-1">FCM Type</p>
                            <div className="flex items-center gap-2">
                                <Smartphone className="w-4 h-4 text-white/60" />
                                <p className="text-white">{selectedNotification.fcm_type}</p>
                            </div>
                        </div>
                    </div>

                    {/* FCM Response */}
                    <div>
                        <p className="text-white/40 text-sm uppercase tracking-wide mb-1">FCM Response</p>
                        <div className={`p-4 rounded-xl border text-sm break-all font-mono
                            ${selectedNotification.STATUS === 'SUCCESS'
                                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                                : selectedNotification.STATUS === 'FAILED'
                                    ? 'bg-red-500/10 border-red-500/30 text-red-400'
                                    : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
                            }`}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <StatusIcon className="w-5 h-5" />
                                <span className="font-semibold tracking-wide">{selectedNotification.STATUS}</span>
                            </div>
                            <p className="leading-relaxed whitespace-pre-wrap">
                                {selectedNotification.fcmResponse || 'N/A'}
                            </p>
                            {selectedNotification.fcm_type === 'BULK' && selectedNotification.fcmResponse && (
                                <div className="mt-3 text-xs opacity-80">📦 Bulk delivery report</div>
                            )}
                        </div>
                    </div>

                    {/* Timestamps */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-white/40 text-sm uppercase mb-1">Date</p>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-white/60" />
                                <p className="text-white">{selectedNotification.date}</p>
                            </div>
                        </div>
                        <div>
                            <p className="text-white/40 text-sm uppercase mb-1">Creation Time</p>
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-white/60" />
                                <p className="text-white">{selectedNotification.creation_time_str}</p>
                            </div>
                        </div>
                    </div>

                    {/* Client Info */}
                    <div>
                        <p className="text-white/40 text-sm uppercase mb-1">Client IP</p>
                        <p className="text-white">{selectedNotification.client_ip || 'N/A'}</p>
                    </div>
                    {selectedNotification.user_agent && (
                        <div>
                            <p className="text-white/40 text-sm uppercase mb-1">User Agent</p>
                            <p className="text-white break-all text-sm">{selectedNotification.user_agent}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}